import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { exportApi } from '../lib/api';
import { TableSkeleton } from '../components/ui/Skeleton';
import Button from '../components/ui/Button';
import { downloadCSV, formatDateTime } from '../lib/utils';

export default function Export() {
  const [exportType, setExportType] = useState<'all' | 'sitewide' | 'page'>('all');
  const [exportFormat, setExportFormat] = useState<'dashboard' | 'jira'>('dashboard');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [exporting, setExporting] = useState(false);

  const { data: logsData, isLoading: logsLoading, refetch } = useQuery({
    queryKey: ['export', 'logs'],
    queryFn: exportApi.logs,
  });

  const logs = logsData?.data || [];

  async function handleExport() {
    setExporting(true);
    try {
      const params: Record<string, string> = { type: exportType, format: exportFormat };
      if (fromDate) params.from = fromDate;
      if (toDate) params.to = toDate;

      const csv = await exportApi.csv(params);
      const prefix = exportFormat === 'jira' ? 'tkxel-jira-import' : 'tkxel-seo';
      const filename = `${prefix}-${exportType}-${new Date().toISOString().split('T')[0]}.csv`;
      downloadCSV(csv, filename);
      toast.success('CSV exported successfully');
      refetch();
    } catch (err: any) {
      toast.error(err.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  }

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Export Options */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-5">Export SEO Tasks</h3>

        {/* Export Format */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-3">Export Format</label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {[
              { value: 'dashboard', label: 'Dashboard Format', description: 'Original CSV layout for internal reporting' },
              { value: 'jira', label: 'Jira Import Format', description: 'Same columns as Jira export — PM can import directly into Jira' },
            ].map(opt => (
              <label key={opt.value} className="flex items-start gap-3 cursor-pointer p-3 rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-colors">
                <input
                  type="radio"
                  name="exportFormat"
                  value={opt.value}
                  checked={exportFormat === opt.value}
                  onChange={() => setExportFormat(opt.value as any)}
                  className="mt-0.5 text-primary-600"
                />
                <div>
                  <p className="text-sm font-medium text-gray-800">{opt.label}</p>
                  <p className="text-xs text-gray-500">{opt.description}</p>
                </div>
              </label>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Export Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">Export Type</label>
            <div className="space-y-2">
              {[
                { value: 'all', label: 'All Tasks', description: 'Sitewide + Page tasks' },
                { value: 'sitewide', label: 'Sitewide Tasks Only', description: 'Technical SEO fixes' },
                { value: 'page', label: 'Page Tasks Only', description: 'Per-page optimizations' },
              ].map(opt => (
                <label key={opt.value} className="flex items-start gap-3 cursor-pointer p-3 rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-colors">
                  <input
                    type="radio"
                    name="exportType"
                    value={opt.value}
                    checked={exportType === opt.value}
                    onChange={() => setExportType(opt.value as any)}
                    className="mt-0.5 text-primary-600"
                  />
                  <div>
                    <p className="text-sm font-medium text-gray-800">{opt.label}</p>
                    <p className="text-xs text-gray-500">{opt.description}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Date Range */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">Date Range (Optional)</label>
            <div className="space-y-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">From</label>
                <input
                  type="date"
                  value={fromDate}
                  onChange={e => setFromDate(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">To</label>
                <input
                  type="date"
                  value={toDate}
                  onChange={e => setToDate(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              {(fromDate || toDate) && (
                <button
                  onClick={() => { setFromDate(''); setToDate(''); }}
                  className="text-sm text-red-500 hover:text-red-700"
                >
                  Clear dates
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="mt-6 pt-5 border-t border-gray-100 flex items-center gap-4">
          <Button onClick={handleExport} loading={exporting} size="lg">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Export as CSV
          </Button>
          <p className="text-sm text-gray-400">
            Exported in UTF-8 CSV format. All timestamps in PKT (UTC+5).
          </p>
        </div>
      </div>

      {/* Export Information */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
        <div className="flex items-start gap-3">
          <svg className="w-5 h-5 text-blue-600 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div className="text-sm text-blue-700">
            <p className="font-semibold mb-1">Scheduled Export</p>
            <p>A scheduled export automatically runs every day at 03:00 PKT (22:00 UTC) via Cloudflare cron trigger. Export logs are recorded below.</p>
          </div>
        </div>
      </div>

      {/* Export History */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100">
          <h3 className="text-base font-semibold text-gray-900">Export History</h3>
        </div>
        {logsLoading ? (
          <TableSkeleton rows={5} cols={4} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50">
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Date & Time (PKT)</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Filename</th>
                  <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Total Tasks</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log: any) => (
                  <tr key={log.id} className="border-t border-gray-50 hover:bg-gray-50">
                    <td className="px-6 py-3 font-mono text-xs text-gray-600">{formatDateTime(log.exported_at)}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        log.export_type === 'scheduled'
                          ? 'bg-purple-100 text-purple-700'
                          : log.export_type?.startsWith('jira')
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-primary-100 text-primary-700'
                      }`}>
                        {log.export_type === 'scheduled' ? '⏰ Scheduled' : log.export_type?.startsWith('jira') ? '📋 Jira' : '👤 Manual'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600 font-mono text-xs">{log.filename}</td>
                    <td className="px-6 py-3 text-right font-medium text-gray-700">{log.total_tasks}</td>
                  </tr>
                ))}
                {logs.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-6 py-10 text-center text-gray-400">
                      No export history yet. Run your first export above.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
